import axios from "axios";
import type { SignedInSessionResource } from "@clerk/types";
import { getUnexpiredToken } from "@/utils/utils";
import type { Storyboard, NavTreeNode } from "@/utils/types";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import { JsxElementType } from "@/utils/jsx/jsxElementTypes";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";

export interface PublishComponentParams {
  storyboard: Storyboard;
  componentName: string;
  canvasId: string;
  session: SignedInSessionResource;
  treeElementLookup: Record<string, JsxElement>;
  onProgress: (progress: number, message: string) => void;
}

export interface PublishComponentResult {
  success: boolean;
  componentId?: string;
  error?: string;
}

/**
 * Traverses the nav tree and collects all component source files
 */
function collectComponentFilesFromNavTree(
  navTree: NavTreeNode | null,
  treeElementLookup: Record<string, JsxElement>,
  collectedFiles: Set<string>,
): void {
  if (!navTree) return;

  const codebaseId = navTree.tempoElement?.codebaseId;
  if (codebaseId) {
    const treeElement = treeElementLookup[codebaseId];

    // If it's a component instance, add its source file
    if (
      treeElement?.type === JsxElementType.ComponentInstance &&
      treeElement.componentSourceImport?.filename
    ) {
      collectedFiles.add(treeElement.componentSourceImport.filename);

      // Also add the file where this component instance is defined (the parent file)
      if (
        treeElement.filename &&
        (treeElement.filename.endsWith(".tsx") ||
          treeElement.filename.endsWith(".jsx") ||
          treeElement.filename.endsWith(".ts") ||
          treeElement.filename.endsWith(".js"))
      ) {
        collectedFiles.add(treeElement.filename);
      }
    }
  }

  // Recursively process children
  if (navTree.children) {
    for (const child of navTree.children) {
      collectComponentFilesFromNavTree(
        child,
        treeElementLookup,
        collectedFiles,
      );
    }
  }
}

/**
 * Gathers all file paths needed for publishing (backend will read contents and parse imports)
 */
function gatherFilePaths(
  storyboard: Storyboard,
  treeElementLookup: Record<string, JsxElement>,
): { filePaths: string[] } | { error: string } {
  if (!storyboard?.fullFilePath) {
    return { error: "Missing storyboard file path" };
  }

  const filePaths = new Set<string>();

  // 1. Add the main storyboard file
  filePaths.add(storyboard.fullFilePath);

  // 2. Get nav tree for this storyboard and collect component files
  const navTree = useCanvasStore.getState().storyboardToNavTree[storyboard.id];
  collectComponentFilesFromNavTree(navTree, treeElementLookup, filePaths);

  return {
    filePaths: Array.from(filePaths),
  };
}

/**
 * Publishes the component to the backend
 */
export async function publishComponent(
  params: PublishComponentParams,
): Promise<PublishComponentResult> {
  const {
    storyboard,
    componentName,
    canvasId,
    session,
    treeElementLookup,
    onProgress,
  } = params;

  try {
    onProgress(10, "Gathering file paths...");

    // Gather file paths (backend will read contents and parse imports)
    const gatherResult = gatherFilePaths(storyboard, treeElementLookup);

    if ("error" in gatherResult) {
      return { success: false, error: gatherResult.error };
    }

    onProgress(30, "Uploading to server...");

    // Get tree element for the storyboard
    const treeElement = storyboard.storyCodebaseId
      ? treeElementLookup[storyboard.storyCodebaseId]
      : null;

    // Make API call to backend
    const supabaseJWT = await getUnexpiredToken(session);
    const response = await axios.post(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${canvasId}/publishComponentV3`,
      {
        main_file: JSON.stringify(storyboard),
        file_paths: gatherResult.filePaths,
        treeElement: treeElement,
        componentName: componentName,
      },
      {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      },
    );

    onProgress(90, "Processing response...");

    const componentId = response.data?.componentId;
    if (componentId) {
      onProgress(100, "Complete!");
      return { success: true, componentId };
    } else {
      return {
        success: false,
        error: "No component ID returned from server",
      };
    }
  } catch (error) {
    console.error("Error publishing component:", error);
    const message =
      axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : error instanceof Error
          ? error.message
          : "An error occurred while publishing";
    return { success: false, error: message };
  }
}
